import type { Platform } from '@/types/Platform';

export type EngagementComponent = 'likes' | 'comments' | 'saves' | 'shares' | 'total_interactions';

export type EngagementCounts = {
  views: number | null | undefined;
  reach?: number | null | undefined;
  likes: number | null | undefined;
  comments: number | null | undefined;
  saves?: number | null | undefined;
  shares?: number | null | undefined;
  totalInteractions?: number | null | undefined;
  /** Pre-summed numerator that is not a platform `total_interactions` value. */
  lumpedEngagements?: number | null | undefined;
  followers?: number | null | undefined;
};

export type EngagementMetrics = {
  avgViews: number | null | undefined;
  avgReach?: number | null | undefined;
  avgLikes: number | null | undefined;
  avgComments: number | null | undefined;
  avgSaves?: number | null | undefined;
  avgShares?: number | null | undefined;
  avgTotalInteractions?: number | null | undefined;
  followerCount: number | null | undefined;
};

export type EngagementBreakdown = {
  likes: number | null;
  comments: number | null;
  saves: number | null;
  shares: number | null;
  providerTotal: number | null;
  includedComponents: EngagementComponent[];
  usedProviderTotal: boolean;
  totalEngagements: number | null;
};

export type EngagementResult = {
  /** Headline rate: engagements ÷ views. Never silently uses reach. */
  post: number | null;
  /** Views ÷ followers. */
  audience: number | null;
  interactionsPerFollower: number | null;
  viewsPerFollower: number | null;
  byViews: number | null;
  byReach: number | null;
  byFollowers: number | null;
  shareRate: number | null;
  saveRate: number | null;
  commentRate: number | null;
  /** Likes + comments ÷ views, only when the headline includes more than that. */
  publicEquivalent: number | null;
  breakdown: EngagementBreakdown;
};

export const EMPTY_ENGAGEMENT_BREAKDOWN: EngagementBreakdown = {
  likes: null,
  comments: null,
  saves: null,
  shares: null,
  providerTotal: null,
  includedComponents: [],
  usedProviderTotal: false,
  totalEngagements: null,
};

export const EMPTY_ENGAGEMENT_RESULT: EngagementResult = {
  post: null,
  audience: null,
  interactionsPerFollower: null,
  viewsPerFollower: null,
  byViews: null,
  byReach: null,
  byFollowers: null,
  shareRate: null,
  saveRate: null,
  commentRate: null,
  publicEquivalent: null,
  breakdown: EMPTY_ENGAGEMENT_BREAKDOWN,
};

const METRIC_LABELS = {
  views: 'views',
  reach: 'reach',
  likes: 'likes',
  comments: 'comments',
  saves: 'saves',
  shares: 'shares',
  totalInteractions: 'total interactions',
  lumpedEngagements: 'engagements',
  followers: 'followers',
} as const;

const readOptionalMetric = (
  value: number | null | undefined,
  label: (typeof METRIC_LABELS)[keyof typeof METRIC_LABELS],
): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Invalid ${label}`);
  }
  return value;
};

const divide = (numerator: number | null, denominator: number | null): number | null => {
  if (numerator === null || denominator === null || denominator <= 0) return null;
  return numerator / denominator;
};

const sumPresent = (values: Array<number | null>): number | null => {
  const present = values.filter((value): value is number => value !== null);
  if (present.length === 0) return null;
  return present.reduce((sum, value) => sum + value, 0);
};

export function toEngagementMetrics(source: {
  avgViews?: number | null;
  avgReach?: number | null;
  avgLikes?: number | null;
  avgComments?: number | null;
  avgSaves?: number | null;
  avgShares?: number | null;
  avgTotalInteractions?: number | null;
  followerCount?: number | null;
}): EngagementMetrics {
  return {
    avgViews: source.avgViews,
    avgReach: source.avgReach,
    avgLikes: source.avgLikes,
    avgComments: source.avgComments,
    avgSaves: source.avgSaves,
    avgShares: source.avgShares,
    avgTotalInteractions: source.avgTotalInteractions,
    followerCount: source.followerCount,
  };
}

export function toEngagementCounts(metrics: EngagementMetrics): EngagementCounts {
  return {
    views: metrics.avgViews,
    reach: metrics.avgReach,
    likes: metrics.avgLikes,
    comments: metrics.avgComments,
    saves: metrics.avgSaves,
    shares: metrics.avgShares,
    totalInteractions: metrics.avgTotalInteractions,
    followers: metrics.followerCount,
  };
}

export function calculateEngagementFromCounts(counts: EngagementCounts): EngagementResult {
  const views = readOptionalMetric(counts.views, METRIC_LABELS.views);
  const reach = readOptionalMetric(counts.reach, METRIC_LABELS.reach);
  const likes = readOptionalMetric(counts.likes, METRIC_LABELS.likes);
  const comments = readOptionalMetric(counts.comments, METRIC_LABELS.comments);
  const saves = readOptionalMetric(counts.saves, METRIC_LABELS.saves);
  const shares = readOptionalMetric(counts.shares, METRIC_LABELS.shares);
  const providerTotal = readOptionalMetric(
    counts.totalInteractions,
    METRIC_LABELS.totalInteractions,
  );
  const lumpedTotal = readOptionalMetric(counts.lumpedEngagements, METRIC_LABELS.lumpedEngagements);
  const followers = readOptionalMetric(counts.followers, METRIC_LABELS.followers);

  const usedProviderTotal = providerTotal !== null;
  const summedComponents = sumPresent([likes, comments, saves, shares]);
  const totalEngagements = usedProviderTotal
    ? providerTotal
    : lumpedTotal !== null
      ? lumpedTotal
      : summedComponents;

  const includedComponents: EngagementComponent[] = usedProviderTotal
    ? ['total_interactions']
    : lumpedTotal !== null
      ? []
      : (
          [
            ['likes', likes],
            ['comments', comments],
            ['saves', saves],
            ['shares', shares],
          ] as const
        )
          .filter(([, value]) => value !== null)
          .map(([name]) => name);

  const byViews = divide(totalEngagements, views);
  const byReach = divide(totalEngagements, reach);
  const byFollowers = divide(totalEngagements, followers);
  const viewsPerFollower = divide(views, followers);

  const publicComponents = sumPresent([likes, comments]);
  const publicEquivalent = divide(publicComponents, views);
  const headlineUsesMoreThanPublic = usedProviderTotal || saves !== null || shares !== null;
  const showPublicEquivalent =
    headlineUsesMoreThanPublic &&
    publicEquivalent !== null &&
    byViews !== null &&
    publicEquivalent !== byViews;

  return {
    post: byViews,
    audience: viewsPerFollower,
    interactionsPerFollower: byFollowers,
    viewsPerFollower,
    byViews,
    byReach,
    byFollowers,
    shareRate: divide(shares, views),
    saveRate: divide(saves, views),
    commentRate: divide(comments, views),
    publicEquivalent: showPublicEquivalent ? publicEquivalent : null,
    breakdown: {
      likes,
      comments,
      saves,
      shares,
      providerTotal,
      includedComponents,
      usedProviderTotal,
      totalEngagements,
    },
  };
}

export function tryCalculateEngagementFromCounts(counts: EngagementCounts): EngagementResult {
  try {
    return calculateEngagementFromCounts(counts);
  } catch {
    // Display-only. Ingest and rollups throw on corrupt values. A single bad
    // featured card must not take down a kit or rate card.
    return EMPTY_ENGAGEMENT_RESULT;
  }
}

export function calculateEngagement(
  _platform: Platform,
  metrics: EngagementMetrics,
): EngagementResult {
  return calculateEngagementFromCounts(toEngagementCounts(metrics));
}
